import type { IntegrationEnhanced } from "@/api/integrations";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useClaimLowerLeftCorner } from "@/context/LowerLeftCornerContext";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useRightPaneExpandContext } from "@/context/RightPaneExpandContext";
import { useIsMobileDevice } from "@/hooks/useIsMobileDevice";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import { IntegrationIcon, type Entity } from "@cohesive/ui-components";
import { Circle } from "lucide-react";
import { motion } from "motion/react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCanvasToolbarLayout } from "../context/CanvasToolbarLayoutContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import type { PlacementItem } from "../hooks/placementItems";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";
import { useCanvasDrawStyle } from "../hooks/useCanvasDrawStyle";
import { useCanvasNoteStyle } from "../hooks/useCanvasNoteStyle";
import { useCanvasShapeAndEdgeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import type { ConnectorPreset } from "../hooks/useConnectorPlacement";
import type { ShapeType } from "../nodes/shapes/index";
import {
  EDGE_COLORS,
  NOTE_COLORS,
  SHAPE_COLORS,
} from "../nodes/shared/CanvasColorPalettes";
import { markOf } from "../nodes/shared/canvasColorRoles";
import {
  CanvasToolbarButton,
  CanvasToolbarGroup,
  CanvasToolbarShell,
  canvasToolbarMountClassName,
} from "./toolbar";
import {
  CanvasNewChat,
  type CanvasNewChatArcKey,
} from "./toolbar/CanvasNewChat";
import {
  CANVAS_CURSOR_MODES,
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
  CANVAS_TOOLBAR_GROUPS,
  CANVAS_TOOL_ICONS,
  CANVAS_TOOL_LABELS,
  type CanvasCursorMode,
  type CanvasToolKey,
} from "./toolbar/canvasToolbarComposition";
import { SHAPE_LUCIDE_ICON } from "./toolbar/canvasToolbarIcon";
import {
  CanvasToolPopover,
  CanvasToolPopoverItem,
} from "./toolbar/CanvasToolPopover";
import type { CanvasInsertMenuActions } from "./toolbar/primary/CanvasInsertMenu";
import { CanvasInsertMenu } from "./toolbar/primary/CanvasInsertMenu";
import { CanvasConnectorEntityBody } from "./toolbar/tray/CanvasConnectorEntityPanel";
import {
  CanvasTray,
  type CanvasTrayCloseReason,
  type CanvasTrayState,
} from "./toolbar/tray/CanvasTray";
import { keepEditorFocus } from "./toolbar/tray/emojiInsert";
import { pickInk, pickName } from "./toolbar/tray/pickInk";
import {
  CANVAS_TRAY_TITLES,
  isCanvasTrayKey,
  type CanvasTrayKey,
} from "./toolbar/tray/trayRegistry";
import { BlocksTray } from "./toolbar/tray/trays/BlocksTray";
import { ConnectorsTray } from "./toolbar/tray/trays/ConnectorsTray";
import { DrawTray } from "./toolbar/tray/trays/DrawTray";
import { ShapesTray } from "./toolbar/tray/trays/ShapesTray";
import { StickersTray } from "./toolbar/tray/trays/StickersTray";
import { StickyTray } from "./toolbar/tray/trays/StickyTray";
import { TextTray } from "./toolbar/tray/trays/TextTray";

interface CanvasToolbarProps {
  onToggleTextNoteMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingTextNote: boolean;
  onToggleDocMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingDoc: boolean;
  onToggleTextLabelMode: () => void;
  isPlacingTextLabel: boolean;
  onToggleSectionMode: () => void;
  isPlacingSection: boolean;
  onCancelPlacement?: () => void;
  /** Close toolbar flyouts and add-context without cancelling placement. */
  onDismissToolbarMenus?: () => void;
  isShapesMenuOpen: boolean;
  isPlacingShape: boolean;
  pendingShapeType: ShapeType | null;
  onToggleShapesMenu: () => void;
  onShapeSelect: (shape: ShapeType) => void;
  isDrawMenuOpen: boolean;
  isDrawing: boolean;
  onToggleDrawMenu: () => void;
  onCloseDrawMenu: () => void;
  isConnectorActive: boolean;
  activeConnectorPreset: ConnectorPreset | null;
  onConnectorSelect: (preset: ConnectorPreset) => void;
  isWidgetsMenuOpen: boolean;
  onWidgetsMenuOpenChange: (open: boolean) => void;
  isLinkMenuOpen: boolean;
  onLinkMenuOpenChange: (open: boolean) => void;
  insertMenu: CanvasInsertMenuActions & {
    onIntegrationEntitySelect?: (entity: Entity) => void;
  };
  /** The New Chat circle: click opens the chat; the arc starts a quick
   * chat, a comment or a voice note where the next click lands. */
  onNewChat: () => void;
  onNewChatSelect: (key: CanvasNewChatArcKey) => void;
  canComment: boolean;
  /** The chat window is up: the circle reads pressed and closes it. */
  chatOpen?: boolean;
  /** A sticker, block, icon or emoji armed from its tray (ROUND2 §3). */
  isPlacingWidget: boolean;
  pendingPlacement: PlacementItem | null;
  onWidgetSelect: (item: PlacementItem) => void;
}

/**
 * The cursor tile's centre from the bar's start edge — its left, or the
 * vertical column's bottom: the shell's hairline, its 2px inset, and half a
 * tile. Derived rather than measured, so the popover cannot drift from the
 * bar between renders.
 */
const CURSOR_ANCHOR = 1 + 2 + 20;

type TrayShown = Exclude<CanvasTrayState, "closed">;

export function CanvasToolbar({
  onToggleTextNoteMode,
  isPlacingTextNote,
  onToggleDocMode,
  isPlacingDoc,
  onToggleTextLabelMode,
  isPlacingTextLabel,
  onToggleSectionMode,
  isPlacingSection,
  onCancelPlacement,
  onDismissToolbarMenus,
  isShapesMenuOpen,
  isPlacingShape,
  pendingShapeType,
  onToggleShapesMenu,
  onShapeSelect,
  isDrawMenuOpen,
  isDrawing,
  onToggleDrawMenu,
  onCloseDrawMenu,
  isConnectorActive,
  activeConnectorPreset,
  onConnectorSelect,
  isWidgetsMenuOpen,
  onWidgetsMenuOpenChange,
  isLinkMenuOpen,
  onLinkMenuOpenChange,
  insertMenu,
  onNewChat,
  onNewChatSelect,
  canComment,
  chatOpen = false,
  isPlacingWidget,
  pendingPlacement,
  onWidgetSelect,
}: CanvasToolbarProps) {
  const {
    tooltipSide,
    containerClassName,
    raisedClassName,
    shed: shedByBand,
    discStandsDown,
    trayFits,
    rowEases,
    placeRow,
  } = useCanvasToolbarLayout();
  // The row's rendered width, for its placement (D53): the shift never
  // changes the width, so there is no loop to close.
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const sync = () => setRowWidth(el.offsetWidth);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const placement = rowWidth > 0 ? placeRow(rowWidth) : null;
  const { cursorTool, setCursorTool, setToolbarTrayDetached } = useCanvasUi();
  const { lastPlacedShape, activeShapeColor, activeShapeNoFill } =
    useCanvasShapeAndEdgeStyle();
  const { drawColor, drawErasing } = useCanvasDrawStyle();
  const { noteColor } = useCanvasNoteStyle();
  const openSettings = useOpenSettings();
  const mobile = useIsMobileDevice();
  // Two reasons to shed to the Cursor shell, kept apart (D29). CAPABILITY:
  // a mobile device reads the canvas, so the authoring groups, the tray and
  // the disc go and the corner FAB keeps the chat; the popover stays with
  // Pointer · Hand. FIT: a band the bar cannot hold (D7) hides the groups
  // but the device still authors — Draw stays in the popover, its tray
  // opens if the band holds it, keys and the context menu still insert.
  // Only the fit shed expands: its Cursor tile opens the whole bar as a
  // vertical column over the chat, its trays beside it.
  const shed = mobile || shedByBand;
  const fitShed = shedByBand && !mobile;
  const [expanded, setExpanded] = useState(false);
  const vertical = expanded && fitShed;
  const showDisc = !mobile && !discStandsDown;
  const disclosures = !mobile && (trayFits || vertical);
  const tileTooltipSide = vertical ? "right" : tooltipSide;
  // The shed row stands in the canvas's lower-left corner, where the rail's
  // avatar stands while the rail is collapsed. Beside a full-width detail
  // pane the corner is the chat launcher's.
  const { isFullWidth: detailCoversCanvas } = useRightPaneExpandContext();
  useClaimLowerLeftCorner(shedByBand && !detailCoversCanvas);
  const cursorModes = mobile
    ? CANVAS_CURSOR_MODES.filter((m) => m.key !== "draw")
    : CANVAS_CURSOR_MODES;

  // ---- the tray ---------------------------------------------------------
  // `tray` is the trigger whose tray is showing, or null; `trayState` is
  // open · popped. The frame outlives the key that closed it so
  // it can slide out with the outgoing content.
  const [tray, setTray] = useState<CanvasTrayKey | null>(null);
  const [trayState, setTrayState] = useState<TrayShown>("open");
  const trayRef = useRef<CanvasTrayKey | null>(null);
  trayRef.current = tray;
  const lastTray = useRef<CanvasTrayKey | null>(null);
  if (tray) lastTray.current = tray;
  const frameKey = tray ?? lastTray.current;
  const docked = tray !== null && trayState !== "popped";

  const [modePopoverOpen, setModePopoverOpen] = useState(false);
  const [entityIntegration, setEntityIntegration] =
    useState<IntegrationEnhanced | null>(null);
  // Picking a connected source pops the Connectors tray by default (D38).
  // Docking it is a preference, not a one-off: until the user pops it out
  // again, later picks open the picker where the tray already is.
  const pickerPops = useRef(true);
  const tileRefs = useRef<Partial<Record<CanvasToolKey, HTMLButtonElement>>>(
    {},
  );
  const tileRefSetters = useRef<
    Partial<Record<CanvasToolKey, (el: HTMLButtonElement | null) => void>>
  >({});
  const tileRef = (key: CanvasToolKey) => {
    const map = tileRefSetters.current;
    if (!map[key]) {
      map[key] = (el) => {
        if (el) tileRefs.current[key] = el;
        else delete tileRefs.current[key];
      };
    }
    return map[key];
  };

  // The page's open flags are the external signal (P opens Shapes, W opens
  // Blocks, Draw opens the pen kit) and the way the page closes a tray; the
  // bar mirrors them into its one tray key.
  const syncFlag = useCallback(
    (key: CanvasTrayKey, open: boolean) => {
      if (key === "shapes") {
        if (open && !isShapesMenuOpen) onToggleShapesMenu();
        if (!open && isShapesMenuOpen) onDismissToolbarMenus?.();
      } else if (key === "blocks" || key === "stickers") {
        if (open !== isWidgetsMenuOpen) onWidgetsMenuOpenChange(open);
      } else if (key === "cursor") {
        if (open && !isDrawMenuOpen) onToggleDrawMenu();
        if (!open && isDrawMenuOpen) onCloseDrawMenu();
      }
    },
    [
      isShapesMenuOpen,
      onToggleShapesMenu,
      onDismissToolbarMenus,
      isWidgetsMenuOpen,
      onWidgetsMenuOpenChange,
      isDrawMenuOpen,
      onToggleDrawMenu,
      onCloseDrawMenu,
    ],
  );

  const closeTray = useCallback(() => {
    const current = trayRef.current;
    if (!current) return;
    syncFlag(current, false);
    setTray(null);
    setTrayState("open");
  }, [syncFlag]);

  // A picker trigger opens its tray. The same trigger again closes it; a
  // different trigger swaps the content inside a frame that never leaves.
  // Popped, the bar has stopped claiming the panel, so a click re-aims it.
  const openTray = useCallback(
    (key: CanvasTrayKey) => {
      const current = trayRef.current;
      setModePopoverOpen(false);
      setEntityIntegration(null);
      if (current === key && trayState !== "popped") {
        closeTray();
        return;
      }
      if (current && current !== key) syncFlag(current, false);
      syncFlag(key, true);
      setTray(key);
      setTrayState(trayState === "popped" ? "popped" : trayState);
    },
    [closeTray, syncFlag, trayState],
  );

  // `tray` is a dep because the flag effects above set it a render after
  // the flag flips; the close must see the key, not the ref's stale null.
  useEffect(() => {
    if (disclosures) return;
    if (tray) closeTray();
    setEntityIntegration(null);
  }, [disclosures, tray, closeTray]);
  // The pen authors: a trackpad detached mid-session disarms it.
  useEffect(() => {
    if (mobile && isDrawing) onCancelPlacement?.();
  }, [mobile, isDrawing, onCancelPlacement]);

  // A popped tray survives board clicks (D22): the page reads this latch
  // before a pane click clears the menu flags behind it (D31).
  useEffect(() => {
    setToolbarTrayDetached(tray !== null && trayState !== "open");
  }, [tray, trayState, setToolbarTrayDetached]);
  useEffect(
    () => () => setToolbarTrayDetached(false),
    [setToolbarTrayDetached],
  );

  // Escape and the chrome X return focus to the trigger tile; a click
  // outside does not — it would steal focus from whatever was clicked.
  const onTrayState = useCallback(
    (next: CanvasTrayState, reason: CanvasTrayCloseReason) => {
      if (next === "closed") {
        const key = trayRef.current ?? lastTray.current;
        setEntityIntegration(null);
        closeTray();
        if (key && (reason === "escape" || reason === "chrome")) {
          tileRefs.current[key]?.focus({ preventScroll: true });
        }
        return;
      }
      if (trayRef.current === "connectors")
        pickerPops.current = next === "popped";
      setTrayState(next);
    },
    [closeTray],
  );

  // The cursor closes whatever is open: reaching for pointer, hand or draw
  // is the user leaving whatever they were placing.
  const closeEverything = useCallback(() => {
    closeTray();
    setEntityIntegration(null);
    onDismissToolbarMenus?.();
    onLinkMenuOpenChange(false);
  }, [closeTray, onDismissToolbarMenus, onLinkMenuOpenChange]);

  const collapse = useCallback(() => {
    closeEverything();
    setModePopoverOpen(false);
    setExpanded(false);
  }, [closeEverything]);

  // The column's modes stand beside the Cursor tile for as long as no tray
  // does; the column's own dismissal covers them, so only Escape closes.
  const closeModePopover = useCallback(
    (reason: "escape" | "outside" | "leave") => {
      if (vertical) {
        if (reason !== "escape") return;
        collapse();
      } else {
        setModePopoverOpen(false);
      }
      if (reason === "escape") {
        tileRefs.current.cursor?.focus({ preventScroll: true });
      }
    },
    [vertical, collapse],
  );

  const pickCursorMode = (mode: CanvasCursorMode) => {
    setModePopoverOpen(false);
    onCancelPlacement?.();
    if (mode === "draw") {
      setCursorTool("arrow");
      openTray("cursor");
      return;
    }
    setCursorTool(mode === "hand" ? "hand" : "arrow");
    if (vertical) collapse();
  };

  // ONE TOOL STATE, DERIVED. The page keeps its placement hooks and the UI
  // context its cursor tool; the bar reads them as one `tool`, so a
  // one-shot placement un-lights its tile on its own and Pointer ships
  // selected — the bar always has exactly one lit tile.
  const tool: CanvasToolKey = isPlacingTextNote
    ? "sticky"
    : isPlacingDoc
      ? "doc"
      : isPlacingSection
        ? "section"
        : isPlacingTextLabel
          ? "text"
          : isPlacingShape || isConnectorActive
            ? "shapes"
            : isPlacingWidget && pendingPlacement
              ? pendingPlacement.tool
              : tray === "blocks" ||
                  tray === "stickers" ||
                  tray === "connectors"
                ? tray
                : entityIntegration
                  ? "connectors"
                  : "cursor";
  // ONE EFFECT DECIDES THE TRAY. The page's open flags name the tray they
  // want (P → Shapes, W → Blocks, Draw → the pen kit) and the bar reacts to
  // a flag CHANGING: turning on opens or swaps in place, turning off closes.
  // The bar-local trays (Sticky, Text, Connectors) follow the armed tool: a
  // docked one closes on the render the tool moved unless a flag asked for
  // other content in that same render — the keyboard re-arm case, which
  // two separate effects raced (D31). A popped tray stays
  // unless a flag asks for other content or its own flag drops — Escape,
  // the chrome X, the Cursor tile and Insert still close everything.
  const lastToolRef = useRef(tool);
  const flagsRef = useRef({ shapes: false, widgets: false, draw: false });
  useEffect(() => {
    const toolMoved = lastToolRef.current !== tool;
    lastToolRef.current = tool;
    const prev = flagsRef.current;
    flagsRef.current = {
      shapes: isShapesMenuOpen,
      widgets: isWidgetsMenuOpen,
      draw: isDrawMenuOpen,
    };
    const current = trayRef.current;
    const turnedOn: CanvasTrayKey | null =
      isShapesMenuOpen && !prev.shapes
        ? "shapes"
        : isWidgetsMenuOpen && !prev.widgets
          ? current === "stickers"
            ? "stickers"
            : "blocks"
          : isDrawMenuOpen && !prev.draw
            ? "cursor"
            : null;
    if (!current) {
      if (turnedOn) setTray(turnedOn);
      return;
    }
    const own =
      current === "shapes"
        ? [isShapesMenuOpen, prev.shapes]
        : current === "blocks" || current === "stickers"
          ? [isWidgetsMenuOpen, prev.widgets]
          : current === "cursor"
            ? [isDrawMenuOpen, prev.draw]
            : null;
    if (own) {
      const [flag, was] = own;
      if (!flag && was) {
        setTray(turnedOn);
        return;
      }
      if (turnedOn && turnedOn !== current) {
        setTray(turnedOn);
        return;
      }
      if (toolMoved && trayState === "open" && tool !== current) closeTray();
      return;
    }
    if (turnedOn) {
      setTray(turnedOn);
      return;
    }
    if (toolMoved && tool !== current && trayState === "open") closeTray();
  }, [
    tool,
    tray,
    trayState,
    isShapesMenuOpen,
    isWidgetsMenuOpen,
    isDrawMenuOpen,
    closeTray,
  ]);

  // THE COLUMN'S DISMISSAL. Escape, a press outside it (a popped tray
  // survives board presses, D22, and keeps the column with it), the shed
  // ending and the chat opening or closing all collapse it, and so does
  // arming a placement with nothing left open beside the column — the next
  // press belongs to the canvas. A tray open with its tool armed keeps the
  // column; the press that places is then the outside press.
  const popped = tray !== null && trayState === "popped";
  useEffect(() => {
    if (!vertical) return undefined;
    // On window: a popover or a drilled-in picker stops its Escape at the
    // document, closing only itself.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const focused = rowRef.current?.contains(document.activeElement);
      collapse();
      if (focused) tileRefs.current.cursor?.focus({ preventScroll: true });
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target || rowRef.current?.contains(target)) return;
      // Insert's menu and the tiles' tooltips portal out of the row.
      if (target.closest?.("[data-radix-popper-content-wrapper]")) return;
      if (popped) return;
      collapse();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [vertical, popped, collapse]);
  useEffect(() => {
    if (expanded && !fitShed) collapse();
  }, [expanded, fitShed, collapse]);
  const lastChatOpen = useRef(chatOpen);
  useEffect(() => {
    if (lastChatOpen.current === chatOpen) return;
    lastChatOpen.current = chatOpen;
    if (expanded) collapse();
  }, [chatOpen, expanded, collapse]);
  const armedBare =
    (isPlacingTextNote ||
      isPlacingDoc ||
      isPlacingSection ||
      isPlacingTextLabel ||
      isPlacingShape ||
      isConnectorActive ||
      isPlacingWidget ||
      isDrawing) &&
    tray === null &&
    !isShapesMenuOpen &&
    !isWidgetsMenuOpen &&
    !isDrawMenuOpen;
  // Keyed on what is armed, so arming Doc over a still-armed pen counts.
  const armedBareKey = armedBare ? (isDrawing ? "draw" : tool) : null;
  const lastArmedBareKey = useRef(armedBareKey);
  useEffect(() => {
    const was = lastArmedBareKey.current;
    lastArmedBareKey.current = armedBareKey;
    if (vertical && armedBareKey && armedBareKey !== was) collapse();
  }, [vertical, armedBareKey, collapse]);

  const cursorMode: CanvasCursorMode = isDrawing
    ? "draw"
    : cursorTool === "hand"
      ? "hand"
      : "pointer";

  const wornShape = pendingShapeType ?? lastPlacedShape;
  const ShapesTriggerIcon = wornShape ? SHAPE_LUCIDE_ICON[wornShape] : Circle;
  const CursorTriggerIcon =
    CANVAS_CURSOR_MODES.find((m) => m.key === cursorMode)?.icon ??
    CANVAS_CURSOR_MODES[0].icon;

  // A tool that owns a chromatic pick wears the colour it will place:
  // Sticky its tint, Shapes its fill, Cursor its ink while the mode is Draw.
  // A neutral pick resolves to null and the tile inherits rust.
  const shapeFillValue = activeShapeNoFill ? null : activeShapeColor;
  const shapesInk = pickInk(shapeFillValue);
  const shapesPick = pickName(shapeFillValue, SHAPE_COLORS);
  const stickyInk = pickInk(noteColor);
  const stickyPick = pickName(noteColor, NOTE_COLORS);
  const drawInkValue = drawErasing ? null : drawColor;
  const cursorInk = cursorMode === "draw" ? pickInk(drawInkValue) : null;
  // The tile's tooltip names the mode and, for Hand, the key that arms it,
  // the way the other tiles name their pick (ROUND3 §4).
  const handMode = CANVAS_CURSOR_MODES.find((m) => m.key === "hand");
  const cursorPick =
    cursorMode === "draw"
      ? pickName(drawInkValue && markOf(drawInkValue), EDGE_COLORS)
      : cursorMode === "hand" && handMode
        ? `${handMode.label} · ${handMode.hint}`
        : null;

  const handleIntegrationEntitySelect = (entity: Entity) => {
    if (insertMenu.onIntegrationEntitySelect) {
      insertMenu.onIntegrationEntitySelect(entity);
    } else {
      insertMenu.onSelectEntity(entity, { x: 0, y: 0 });
    }
  };

  // Arming a place-tool: the same tile again toggles its tray and leaves
  // the tool armed (Escape or the Cursor tile disarms).
  const arm = (
    key: CanvasToolKey,
    start: () => void,
    trayKey?: CanvasTrayKey,
  ) => {
    setModePopoverOpen(false);
    setEntityIntegration(null);
    onLinkMenuOpenChange(false);
    if (tool !== key) {
      if (!trayKey) closeTray();
      onCancelPlacement?.();
      start();
    }
    if (trayKey) openTray(trayKey);
  };

  // Settings is a modal; the raised column must not stand over it.
  const manageConnectors = useCallback(() => {
    if (vertical) collapse();
    openSettings(ROUTES.SETTINGS.INTEGRATIONS);
  }, [vertical, collapse, openSettings]);
  // The connector's own settings detail — the connect flow for one not yet
  // connected — the call useConnectionsCore.openConnectorSettings makes
  // (ROUND2 §5).
  const connectIntegration = useCallback(
    (integration: IntegrationEnhanced) => {
      closeTray();
      if (vertical) collapse();
      openSettings(
        route(ROUTES.SETTINGS.INTEGRATION_SETTINGS, {
          integrationId: integration.id,
        }),
      );
    },
    [closeTray, vertical, collapse, openSettings],
  );

  const toolbarInsertMenu = useMemo(
    () => ({
      ...insertMenu,
      onAddLinkSubmit: (url: string) => {
        insertMenu.onAddLinkSubmit(url);
      },
    }),
    [insertMenu],
  );

  // A plate arms its widget; the armed plate again closes the tray and
  // leaves it armed, the re-click rule every tray follows (D12).
  const armedWidget = isPlacingWidget ? (pendingPlacement?.id ?? null) : null;
  const pickWidget = (item: PlacementItem) => {
    if (armedWidget === item.id) {
      closeTray();
      return;
    }
    onWidgetSelect(item);
  };

  // A popped tray's New row arms its tool and never closes the tray: the
  // tile already armed it on open, so a press while armed changes nothing.
  const newRow = (armed: boolean, start: () => void) =>
    trayState === "popped"
      ? {
          armed,
          onSelect: () => {
            if (armed) return;
            onCancelPlacement?.();
            start();
          },
        }
      : undefined;

  const trayBody = (key: CanvasTrayKey) => {
    switch (key) {
      case "cursor":
        return <DrawTray />;
      case "sticky":
        return (
          <StickyTray
            newNote={newRow(isPlacingTextNote, () => onToggleTextNoteMode())}
          />
        );
      case "text":
        return (
          <TextTray
            newLabel={newRow(isPlacingTextLabel, onToggleTextLabelMode)}
          />
        );
      case "shapes":
        return (
          <ShapesTray
            shape={pendingShapeType ?? lastPlacedShape}
            onShape={onShapeSelect}
            connectorPreset={isConnectorActive ? activeConnectorPreset : null}
            onConnector={onConnectorSelect}
          />
        );
      case "blocks":
        return <BlocksTray value={armedWidget} onSelect={pickWidget} />;
      case "stickers":
        return <StickersTray value={armedWidget} onSelect={pickWidget} />;
      case "connectors":
        if (entityIntegration) {
          return (
            <CanvasConnectorEntityBody
              integration={entityIntegration}
              onEntitySelect={(entity) => {
                handleIntegrationEntitySelect(entity);
                // Back to the roster; the tray stays popped so the next
                // pick is one click away.
                setEntityIntegration(null);
              }}
            />
          );
        }
        return (
          <ConnectorsTray
            onPickConnected={(integration) => {
              // The picker is the popped tray's body (ROUND3 §2): pop if
              // docked, keep the seat if already popped — unless the user
              // has docked it, which keeps it docked.
              setEntityIntegration(integration);
              if (pickerPops.current) setTrayState("popped");
            }}
            onConnect={connectIntegration}
            onManage={manageConnectors}
          />
        );
    }
  };

  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  const renderTool = (key: CanvasToolKey) => {
    const label = CANVAS_TOOL_LABELS[key];
    const Glyph = CANVAS_TOOL_ICONS[key];
    const glyph = (Icon: typeof Circle) => (
      <Icon
        size={CANVAS_TILE_GLYPH}
        strokeWidth={CANVAS_GLYPH_STROKE}
        aria-hidden="true"
      />
    );
    // aria-expanded goes false once popped, the act of severing the panel
    // from its trigger.
    const trayOpen = (k: CanvasTrayKey) => tray === k && trayState !== "popped";

    switch (key) {
      case "cursor":
        return (
          <CanvasToolbarButton
            key={key}
            ref={tileRef(key)}
            size="tile"
            label={label}
            pick={cursorPick}
            ink={cursorInk}
            tooltipSide={tileTooltipSide}
            active={tool === "cursor"}
            data-tool={key}
            data-popover-trigger=""
            aria-haspopup={fitShed ? undefined : "menu"}
            aria-expanded={fitShed ? vertical : modePopoverOpen}
            onClick={() => {
              if (vertical) {
                collapse();
                return;
              }
              closeEverything();
              if (fitShed) {
                setModePopoverOpen(false);
                setExpanded(true);
                return;
              }
              setModePopoverOpen((v) => !v);
            }}
          >
            {glyph(CursorTriggerIcon)}
          </CanvasToolbarButton>
        );
      case "sticky":
        return (
          <CanvasToolbarButton
            key={key}
            ref={tileRef(key)}
            size="tile"
            label={label}
            pick={stickyPick}
            ink={stickyInk}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            aria-haspopup="dialog"
            aria-expanded={trayOpen("sticky")}
            onClick={(e) =>
              arm(
                key,
                () => onToggleTextNoteMode({ x: e.clientX, y: e.clientY }),
                "sticky",
              )
            }
          >
            {Glyph && glyph(Glyph)}
          </CanvasToolbarButton>
        );
      case "doc":
        return (
          <CanvasToolbarButton
            key={key}
            size="tile"
            label={label}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            onClick={(e) =>
              arm(key, () => onToggleDocMode({ x: e.clientX, y: e.clientY }))
            }
          >
            {Glyph && glyph(Glyph)}
          </CanvasToolbarButton>
        );
      case "section":
        return (
          <CanvasToolbarButton
            key={key}
            size="tile"
            label={label}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            onClick={() => arm(key, onToggleSectionMode)}
          >
            {Glyph && glyph(Glyph)}
          </CanvasToolbarButton>
        );
      case "text":
        return (
          <CanvasToolbarButton
            key={key}
            ref={tileRef(key)}
            size="tile"
            label={label}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            aria-haspopup="dialog"
            aria-expanded={trayOpen("text")}
            onClick={() => arm(key, onToggleTextLabelMode, "text")}
          >
            {Glyph && glyph(Glyph)}
          </CanvasToolbarButton>
        );
      case "shapes":
        return (
          <CanvasToolbarButton
            key={key}
            ref={tileRef(key)}
            size="tile"
            label={label}
            pick={shapesPick}
            ink={shapesInk}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            aria-haspopup="dialog"
            aria-expanded={trayOpen("shapes")}
            onClick={() => {
              setModePopoverOpen(false);
              setEntityIntegration(null);
              onLinkMenuOpenChange(false);
              if (tool !== "shapes") onCancelPlacement?.();
              openTray("shapes");
            }}
          >
            {glyph(ShapesTriggerIcon)}
          </CanvasToolbarButton>
        );
      case "blocks":
      case "stickers":
      case "connectors":
        return (
          <CanvasToolbarButton
            key={key}
            ref={tileRef(key)}
            size="tile"
            label={label}
            tooltipSide={tileTooltipSide}
            active={tool === key}
            data-tool={key}
            data-onboarding-target={
              key === "connectors" ? "connections-entry" : undefined
            }
            aria-haspopup="dialog"
            aria-expanded={trayOpen(key)}
            onPointerDown={key === "stickers" ? keepEditorFocus : undefined}
            onClick={() => {
              onLinkMenuOpenChange(false);
              onCancelPlacement?.();
              openTray(key);
            }}
          >
            {Glyph && glyph(Glyph)}
          </CanvasToolbarButton>
        );
      case "insert":
        return (
          <CanvasInsertMenu
            key={key}
            variant="toolbar"
            register="toolbar"
            size="tile"
            tooltipSide={vertical ? "right" : undefined}
            menuSide={vertical ? "right" : undefined}
            onOpen={() => {
              // Insert is the bar's one menu; opening it closes a docked
              // tray, because the bar shows one disclosure at a time.
              if (docked) closeTray();
              setModePopoverOpen(false);
              setEntityIntegration(null);
            }}
            isWidgetsMenuOpen={false}
            isLinkMenuOpen={isLinkMenuOpen}
            onLinkMenuOpenChange={(next) => {
              if (next) {
                onCancelPlacement?.();
                closeTray();
              }
              onLinkMenuOpenChange(next);
            }}
            onManageConnectors={manageConnectors}
            {...toolbarInsertMenu}
          />
        );
    }
  };

  return (
    <motion.div
      ref={rowRef}
      initial={shouldAnimateChromeEnter ? { opacity: 0, y: 20 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
      className={cn(
        containerClassName,
        vertical && raisedClassName,
        // The row, its docked tray and every gap between them are one hit
        // region with one cursor; a placement's cursor-none on the canvas
        // wrapper must not reach through the housing.
        "cursor-pointer",
        // Until the row is measured it centres by class, as it always did.
        !placement && "left-1/2 -translate-x-1/2",
      )}
      style={placement ? { left: placement.left } : undefined}
    >
      <TooltipProvider>
        {/* The mount's box is the bar's box, so the tray and the popover
            centre on the bar and pin to its top edge. Disclosures come before
            the bar in the DOM so the bar paints last and occludes the tuck. */}
        <div
          className={cn(
            canvasToolbarMountClassName,
            // The column rises from the tile's seat: the 46 bar centred
            // in the disc's 50 row stands 2 off its foot.
            vertical && "mb-0.5 self-end",
          )}
        >
          <CanvasToolPopover
            open={vertical ? !docked : modePopoverOpen}
            onClose={closeModePopover}
            label="Cursor mode"
            anchor={CURSOR_ANCHOR}
            side={vertical ? "right" : "top"}
          >
            {cursorModes.map((mode) => (
              <CanvasToolPopoverItem
                key={mode.key}
                label={mode.label}
                hint={mode.hint}
                icon={mode.icon}
                pressed={cursorMode === mode.key}
                onSelect={() => pickCursorMode(mode.key)}
              />
            ))}
          </CanvasToolPopover>

          {frameKey && disclosures && (
            <CanvasTray
              title={CANVAS_TRAY_TITLES[frameKey]}
              state={tray ? trayState : "closed"}
              onState={onTrayState}
              memoryKey={frameKey}
              head={
                frameKey === "connectors" && entityIntegration
                  ? {
                      lead: (
                        <IntegrationIcon
                          platform={entityIntegration.platform}
                          name={entityIntegration.name}
                          imageURL={entityIntegration.metadata?.image_url}
                          className="size-4 rounded"
                        />
                      ),
                      label: entityIntegration.name,
                      backLabel: "Back to connectors",
                      onBack: () => setEntityIntegration(null),
                    }
                  : undefined
              }
              // Portrait beside the column, the picker stays narrow.
              wide={
                frameKey === "connectors" && !!entityIntegration && !vertical
              }
              reflowKey={placement?.left}
              barLeft={placement?.left}
              barEases={rowEases !== false}
              beside={vertical}
            >
              {trayBody(frameKey)}
            </CanvasTray>
          )}

          <CanvasToolbarShell
            register="primary"
            orientation={vertical ? "vertical" : "horizontal"}
            className="z-(--z-dropdown)"
            aria-label="Canvas tools"
          >
            {CANVAS_TOOLBAR_GROUPS.map((group) => (
              // The shed hides every group but Cursor rather than
              // collapsing them; expanded, they stand as a column.
              <CanvasToolbarGroup
                key={group.key}
                well
                orientation={vertical ? "vertical" : "horizontal"}
                className={
                  group.key !== "cursor" && shed && !vertical
                    ? "hidden"
                    : undefined
                }
                data-group={group.key}
                data-onboarding-target={
                  group.key === "capture" ? "canvas-object-tools" : undefined
                }
              >
                {group.tools.map(renderTool)}
              </CanvasToolbarGroup>
            ))}
          </CanvasToolbarShell>
        </div>

        {/* The bar's sibling in a different shape, 16 off its right end on the
            same centreline. The fan is disarmed while a tray is docked — the
            two are mutually exclusive by state — and re-armed when popped. The circle is the chat's resting state and the corner
            FAB stands down, so the onboarding target rides here; on a mobile
            device both return to the FAB. */}
        {showDisc && (
          <CanvasNewChat
            // Kept in the row while the column is up, so the row's width
            // and its seat hold; a tray beside the column covers its spot.
            className={cn("ml-4", vertical && "invisible")}
            disarmed={docked}
            chatOpen={chatOpen}
            canComment={canComment}
            onNewChat={onNewChat}
            onSelect={onNewChatSelect}
            onboardingTargetId="agent-chat-toggle"
          />
        )}
      </TooltipProvider>
    </motion.div>
  );
}

export { isCanvasTrayKey };
